'use client';

import { IReviewsData } from '@/api/types/product';
import { IProductDetail } from '@/api/types/public';
import LensDetails from '@/components/GoodsDetail/LensDetails';
import ProductParams from '@/components/GoodsDetail/ProductParams';
import ShippingReturn from '@/components/GoodsDetail/ShippingReturn';
import { colors, font } from '@/theme/zeelool.theme';
import { scrollToReviewList } from '@/utils/goods-detail-utils';
import { numberUnitFormat } from '@/utils/tools';
import { Box, Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useMemo, useState } from 'react';
import TabDescription from './TabDescription';
import { useTranslations } from 'next-intl';

interface ProductTabProps {
  detail: IProductDetail | any;
  reviewsData: IReviewsData;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

// Tab 配置接口
interface TabConfig {
  value: string;
  label: string;
  condition: (detail: any, reviewsData: IReviewsData, showContentFreight: boolean) => boolean;
  component: React.ReactNode;
  eventName: string;
}

// 自定义样式的 Tabs 组件
const StyledTabs = styled(Tabs)(() => ({
  '& .MuiTabs-scroller': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: '8px',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: colors.primaryBlack,
  },
  '& .MuiTab-root': {
    fontWeight: 'normal',
    fontSize: 'max(1.04vw, 18px)',
    color: '#282828',
    lineHeight: '1.25vw',
    height: '1.25vw',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    minWidth: 'auto',
    '&.Mui-selected': {
      fontFamily: font.bb,
      fontSize: 'min(max(1.46vw, 20px), 30px)',
      color: colors.primaryBlack,
      lineHeight: '1.77vw',
      height: '1.77vw',
    },
  },
}));

const ProductTab: React.FC<ProductTabProps> = ({
  detail,
  reviewsData,
  SDProductDetailsPageBtnClick,
}) => {
  const t = useTranslations('goodsDetail');
  const showContentFreight = useMemo(() => {
    return detail?.product_content_freight?.status === 1;
  }, [detail?.product_content_freight?.status]);

  const reviewNumTitle = useMemo(() => {
    const name = numberUnitFormat(reviewsData.count);
    return `${t('customerReivewsList.reviews')}(${name})`;
  }, [reviewsData.count, t]);

  // Tab 配置数组 - 统一管理所有 tab
  const tabConfigs: TabConfig[] = useMemo(
    () => [
      {
        value: 'productDetails',
        label: t('productDetails.title'),
        condition: (_detail) => _detail.is_glass === 1,
        component: (
          <ProductParams
            detail={detail}
            variant="tab"
            SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          />
        ),
        eventName: 'Product Details',
      },
      {
        value: 'description',
        label: t('productDescription.title'),
        condition: (_detail) => _detail.show_description,
        component: <TabDescription detail={detail} />,
        eventName: 'Product Description',
      },
      {
        value: 'prescriptionDetail',
        label: t('lensDetails.title'),
        condition: (_detail) => _detail.lens_details && _detail.is_glass === 1,
        component: <LensDetails detail={detail} variant="tab" />,
        eventName: 'Lens Details',
      },
      {
        value: 'shippingReturn',
        label: t('shippingReturn.title'),
        condition: (_detail, _reviewsData, _showContentFreight) =>
          _showContentFreight && _detail.is_glass === 1,
        component: <ShippingReturn detail={detail} variant="tab" />,
        eventName: 'Shipping&Returns',
      },
      {
        value: 'Reviews',
        label: reviewNumTitle,
        condition: (_detail, _reviewsData) => _reviewsData.count > 0,
        component: null, // Reviews 不在这里渲染，只是滚动到指定位置
        eventName: 'Reviews',
      },
    ],
    [detail, reviewsData, showContentFreight, reviewNumTitle, SDProductDetailsPageBtnClick, t],
  );

  // 过滤出需要显示的 tabs
  const visibleTabs = useMemo(() => {
    return tabConfigs.filter((tab) => tab.condition(detail, reviewsData, showContentFreight));
  }, [tabConfigs, detail, reviewsData, showContentFreight]);

  const defaultTab = useMemo(() => {
    const firstVisibleTab = visibleTabs[0];
    return firstVisibleTab ? firstVisibleTab.value : '';
  }, [visibleTabs]);

  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const [currActiveTab, setCurrActiveTab] = useState<string>(defaultTab);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      // 找到对应的 tab 配置
      const tabConfig = tabConfigs.find((tab) => tab.value === newValue);

      if (tabConfig) {
        if (tabConfig.value === 'Reviews') {
          SDProductDetailsPageBtnClick?.('View More Reviews Tab');
        } else {
          SDProductDetailsPageBtnClick?.(tabConfig.eventName);
        }
      }

      if (newValue === 'Reviews') {
        scrollToReviewList(110);
        return;
      }
      setActiveTab(newValue);
      setCurrActiveTab(newValue);
    },
    [currActiveTab, SDProductDetailsPageBtnClick, tabConfigs],
  );

  // 如果没有可显示的 tabs 或仅有Reviews，则不渲染
  if (
    visibleTabs.length === 0 ||
    (visibleTabs.length === 1 && visibleTabs[0].value.toLowerCase() === 'reviews')
  ) {
    return null;
  }

  return (
    <div className="product-desc mx-[2.5%] mt-[5vw] mb-[5vw]">
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons={false}
        allowScrollButtonsMobile
      >
        {visibleTabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </StyledTabs>

      <Box sx={{ mt: 2 }}>
        {visibleTabs.map(
          (tab) =>
            tab.component && (
              // seo要求：都渲染在文档里。不展示时使用css隐藏
              <div key={tab.value} className={`${activeTab === tab.value ? 'block' : 'hidden'}`}>
                {tab.component}
              </div>
            ),
        )}
      </Box>
    </div>
  );
};

export default ProductTab;
